'use client';

import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useEmbedding } from '@/components/embed-provider';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ScrollArea } from '../../../components/ui/scroll-area';
import { tenantHooks } from '../../../hooks/tenant-hooks';

function TenantSwitcher() {
  const queryClient = useQueryClient();
  const { data: tenants } = tenantHooks.useTenants();
  const [open, setOpen] = React.useState(false);
  const { embedState } = useEmbedding();
  const { data: currentTenant, setCurrentTenant } =
    tenantHooks.useCurrentTenant();
  const filterTenants = React.useCallback(
    (tenantId: string, search: string) => {
      const tenant = tenants?.find(
        (tenant) => tenant.id.toLowerCase() === tenantId,
      );
      if (!tenant) {
        return 0;
      }
      return tenant.name.toLowerCase().includes(search.toLowerCase())
        ? 1
        : 0;
    },
    [tenants],
  );
  const sortedTenants = (tenants ?? []).sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  if (embedState.isEmbedded) {
    return null;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select a tenant"
          className="w-[200px] justify-between"
        >
          <span className="truncate">{currentTenant?.name}</span>
          <CaretSortIcon className="ml-auto size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command filter={filterTenants}>
          <CommandList>
            <CommandInput placeholder="Search tenants..." />
            <CommandEmpty>No tenants found.</CommandEmpty>
            <CommandGroup key="tenants" heading="Tenants">
              <ScrollArea viewPortClassName="max-h-[200px]">
                {sortedTenants?.map((tenant) => (
                    <CommandItem
                      key={tenant.id}
                      onSelect={() => {
                        setCurrentTenant(
                            queryClient,
                          tenant,
                        );
                        setOpen(false);
                      }}
                      value={tenant.id}
                      className="text-sm"
                    >
                      {tenant.name}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          currentTenant?.id === tenant.id
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { TenantSwitcher };
